/**
 * Budget usage widget — daily and monthly spend against the project's limits.
 *
 * The meter is a plain `<div>` pair rather than MUI's `LinearProgress` because the only thing that
 * component was contributing was a colour ramp keyed off `color="success" | "warning" | "error"`,
 * and those three states are design tokens (`--color-success` / `--color-warning` / `--color-error`)
 * that a modifier class expresses directly. The bar also carries the `progressbar` role and its
 * `aria-value*` attributes, which the MUI version did not.
 */

import type { CSSProperties } from 'react';

import { FormSection, LoadingSkeleton } from '@coordio/ui/layout';

import type { BudgetUsageDto } from '../../types/llmMetrics';

import './dashboardWidgets.scss';

interface BudgetUsageCardProps {
  data: BudgetUsageDto | null;
  loading: boolean;
}

/** Spend crossing the limit is an error, three-quarters through it is a warning. */
const fillModifier = (percentUsed: number): string => {
  if (percentUsed >= 100) return 'budget-usage__fill--over';
  if (percentUsed >= 75) return 'budget-usage__fill--warning';
  return '';
};

const currency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);

export const BudgetUsageCard = ({ data, loading }: BudgetUsageCardProps) => {
  if (loading) {
    return (
      <FormSection title="Budget usage" description="Spend against the project's daily and monthly limits.">
        <LoadingSkeleton rows={2} />
      </FormSection>
    );
  }

  // Rendering nothing without data is deliberate and pre-existing: the dashboard only fetches
  // budget usage once a project is selected, so an empty state here would appear on every
  // tenant-only view and read as a failure rather than as "not applicable".
  if (!data) return null;

  const budgets = [
    { key: 'daily', label: 'Daily budget', spend: data.dailySpend, limit: data.dailyLimit, pct: data.dailyPercentUsed },
    { key: 'monthly', label: 'Monthly budget', spend: data.monthlySpend, limit: data.monthlyLimit, pct: data.monthlyPercentUsed },
  ];

  return (
    <FormSection title="Budget usage" description="Spend against the project's daily and monthly limits.">
      <div className="budget-usage" data-testid="budget-usage">
        {budgets.map((budget) => {
          const percentUsed = budget.pct * 100;
          // Width is capped so an over-budget meter stays inside its track; the caption and the
          // error colour are what communicate the overrun.
          const fillStyle: CSSProperties = { width: `${Math.min(Math.max(percentUsed, 0), 100)}%` };

          return (
            <div className="budget-usage__meter" key={budget.key}>
              <div className="budget-usage__head">
                <span className="budget-usage__label">{budget.label}</span>
                <span className="budget-usage__amount">
                  {currency(budget.spend)} / {currency(budget.limit)}
                </span>
              </div>
              <div
                className="budget-usage__track"
                role="progressbar"
                aria-label={budget.label}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(percentUsed)}
              >
                <div className={`budget-usage__fill ${fillModifier(percentUsed)}`.trim()} style={fillStyle} />
              </div>
              <span className="budget-usage__caption">{percentUsed.toFixed(1)}% used</span>
            </div>
          );
        })}
      </div>
    </FormSection>
  );
};
